import i18n from "../../../i18n";
import { Trans } from "react-i18next";
import { useState, useEffect } from "react";
import { cn } from "../../../lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import {
  todayISO,
  toDateInputValue,
  toTimeInputValue,
  TIME_OPTIONS,
  DEFAULT_END_TIME,
} from "../constants/pollOptions";

/**
 * The two post-publish controls HR retains on a live poll. Everything else
 * about a published poll is frozen — corrections require a new poll.
 */

/** Extend End Date — date + time, never into the past, never on a Closed poll. */
export function ExtendEndDateModal({ open, currentEnd, onConfirm, onClose, loading }) {
  const [date, setDate] = useState("");
  const [time, setTime] = useState(DEFAULT_END_TIME);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setDate("");
    setTime(toTimeInputValue(currentEnd) || DEFAULT_END_TIME);
    setError("");
  }, [open, currentEnd]);

  const handleConfirm = () => {
    if (!date) {
      setError("Pick a new end date.");
      return;
    }
    const next = new Date(`${date}T${time}`);
    if (next <= new Date()) {
      setError("The new end must be in the future.");
      return;
    }
    if (currentEnd && next <= new Date(currentEnd)) {
      setError("The new end must be later than the current one.");
      return;
    }
    // Naive local wall-clock (no toISOString/UTC) — the backend field is a
    // Java LocalDateTime, same as the create form's end date.
    onConfirm(`${date}T${time}:00`);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-[#0a1230]/50 backdrop-blur-sm px-4">
      <div className="w-full max-w-[440px] bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] border border-white/60 font-sans">
        <div className="px-6 py-5">
          <h3 className="text-[17px] font-bold text-foreground mb-3">
            <Trans i18nKey="dateTime.extendEndDate" />
          </h3>
          <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-1.5">
            <Trans i18nKey="dateTime.newEndDateTime" /> <span className="text-[var(--color-error)]">*</span>
          </label>
          <div className="flex gap-2">
            <input
              type="date"
              min={toDateInputValue(currentEnd) || todayISO()}
              value={date}
              onChange={(e) => {
                setDate(e.target.value);
                setError("");
              }}
              className={cn(
                "flex-1 h-10 px-3 rounded-xl border bg-white text-sm outline-none focus:ring-1 focus:ring-[var(--color-accent)]",
                error ? "border-[var(--color-error)]" : "border-[var(--color-text-border)]"
              )} />

            <Select value={time} onValueChange={setTime}>
              <SelectTrigger className="h-10 w-[120px] shrink-0 rounded-xl bg-white border-[var(--color-text-border)]">
                <SelectValue placeholder={i18n.t("dateTime.time")} />
              </SelectTrigger>
              <SelectContent className="max-h-[240px]">
                {TIME_OPTIONS.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {error && <p className="text-[11px] text-[var(--color-error)] mt-1">{error}</p>}
        </div>
        <div className="flex justify-end gap-2.5 px-6 py-4 border-t border-[var(--color-text-border)]/70">
          <button
            type="button"
            onClick={onClose}
            className="h-10 px-5 rounded-xl border border-[var(--color-text-border)] bg-white text-sm font-semibold text-foreground hover:bg-[var(--color-workspace)]">
            <Trans i18nKey="formActions.cancel" />
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={loading}
            className="h-10 px-5 rounded-xl bg-[var(--color-primary)] text-white text-sm font-semibold hover:bg-[var(--color-accent)] disabled:opacity-50">
            {loading ? "Saving…" : "Extend"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Close Poll — ending voting early is irreversible. */
export function ClosePollDialog({ open, onConfirm, onClose, loading }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-[#0a1230]/50 backdrop-blur-sm px-4">
      <div className="w-full max-w-[420px] bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] border border-white/60 font-sans">
        <div className="px-6 py-5">
          <h3 className="text-[17px] font-bold text-foreground mb-2">
            <Trans i18nKey="formActions.closeThisPollNow" />
          </h3>
          <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">
            <Trans i18nKey="polls.votingEndsImmediatelyAndNoFurtherResponsesCan" />
          </p>
        </div>
        <div className="flex justify-end gap-2.5 px-6 py-4 border-t border-[var(--color-text-border)]/70">
          <button
            type="button"
            onClick={onClose}
            className="h-10 px-5 rounded-xl border border-[var(--color-text-border)] bg-white text-sm font-semibold text-foreground hover:bg-[var(--color-workspace)]">
            <Trans i18nKey="formActions.cancel" />
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="h-10 px-5 rounded-xl bg-[var(--color-error)] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50">
            {loading ? "Closing…" : "Close Poll"}
          </button>
        </div>
      </div>
    </div>
  );
}
